import { useCallback, useEffect, useReducer } from 'react';

import Placeholder from '../../assets/images/placeholder.jpg';
import configData from '../../config.json';
import { trackAppEvent } from '../../services/analytics/appInsights';

import { authenticate } from '../../services/api/authApi';
import {
  fetchWatchData,
  generateAssociationCode,
} from '../../services/api/watchDataApi';
import { fetchTranslations } from '../../services/api/translationsApi';
import { fetchProductImageUrl } from '../../services/api/productApi';

import { clearAuthParams, hasAnyAuthParams } from '../../utils/app/queryParams';
import { IS_DEV } from '../../utils/helpers/constants.helper';
import { useAuthParams } from '../useAuthParams';

import { initialState, reducer } from './reducer';
import { extractRequestMetadata, waitForMinimumLoadingTime } from './utils';

const UI_LOADING_MIN_DURATION_MS = 1000;

// Shared module-level state for eager loading promises
let earlyPromise = null;
let earlyPromiseStartedAt = null;

/**
 * Synchronous utility to parse query parameters from the hash or query string
 * during the early document parsing stage before React hooks have mounted.
 */
export const getAuthParamsFromUrl = () => {
  const params = {};

  const searchParams = new URLSearchParams(window.location.search);
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }

  const hashParts = window.location.hash.split('?');
  if (hashParts.length > 1) {
    const hashParams = new URLSearchParams(hashParts[1]);
    for (const [key, value] of hashParams.entries()) {
      params[key] = value;
    }
  }

  return params;
};

/**
 * Resolves the JWT token early without React context dependencies.
 */
async function resolveTokenEarly(authParams) {
  const storedToken = sessionStorage.getItem('bearer');

  if (storedToken) {
    return storedToken;
  }

  if (IS_DEV) {
    const devToken = 'dev';
    sessionStorage.setItem('bearer', devToken);
    return devToken;
  }

  if (!hasAnyAuthParams(authParams)) {
    window.location.replace(configData.redirectUrl);
    return null;
  }

  const authResult = await authenticate(authParams);
  sessionStorage.setItem('bearer', authResult.token);

  return authResult.token;
}

/**
 * Initiates the authentication, watch configuration, translation, and image URL requests.
 * Triggers a native responsive preload of the LCP watch image immediately on resolution.
 */
export const startEarlyInitialization = (authParams) => {
  if (earlyPromise) return earlyPromise;

  earlyPromiseStartedAt = Date.now();
  earlyPromise = (async () => {
    try {
      const token = await resolveTokenEarly(authParams);

      if (!token) {
        return { tokenResolved: false };
      }

      const { cardType } = authParams || {};
      let watchData = null;
      let association = null;
      let businessData = null;
      let productImgUrl = Placeholder;
      let metadataSources = [];

      try {
        const watchResult = await fetchWatchData(token);
        watchData = watchResult.data;
        metadataSources.push(watchResult);

        const rmc =
          watchData?.Configuration?.Rmc || watchData?.Configuration?.Model;

        const detailResults = await Promise.allSettled([
          fetchTranslations(token, rmc),
          watchData?.Configuration?.Rmc
            ? fetchProductImageUrl(token, watchData.Configuration.Rmc)
            : Promise.resolve(Placeholder),
        ]);

        const [translationsResult, imageResult] = detailResults;

        if (translationsResult.status === 'rejected') {
          throw translationsResult.reason;
        }

        businessData = translationsResult.value;

        if (imageResult.status === 'fulfilled' && imageResult.value) {
          productImgUrl = imageResult.value;

          // Inject early LCP responsive image preload tag defensively
          try {
            let urls = [];

            if (Array.isArray(productImgUrl)) {
              urls = productImgUrl.filter(Boolean);
            } else if (typeof productImgUrl === 'string' && productImgUrl) {
              urls = productImgUrl
                .split(',')
                .map((url) => url.trim())
                .filter(Boolean);
            }

            if (urls.length > 0) {
              // Construct the responsive srcset string for early preloading
              const imageSrcset = urls
                .map((url) => {
                  const match = url.match(/w_(\d+)/);
                  const width = match ? Number(match[1]) : null;
                  return width ? `${url} ${width}w` : null;
                })
                .filter(Boolean)
                .join(', ');

              if (imageSrcset) {
                const preloadLink = document.createElement('link');
                preloadLink.rel = 'preload';
                preloadLink.as = 'image';
                preloadLink.fetchPriority = 'high';

                // Set native responsive preloading attributes matching the rendering sizes
                preloadLink.setAttribute('imagesrcset', imageSrcset);
                preloadLink.setAttribute(
                  'imagesizes',
                  '(max-width: 768px) 100vw, (max-width: 1580px) 50vw, 1580px',
                );

                document.head.appendChild(preloadLink);
              }
            }
          } catch (e) {
            console.warn('Eager watch image preload injection failed', e);
          }
        }
      } catch (requestError) {
        if (requestError?.status !== 404) {
          throw requestError;
        }

        if ((cardType || '').toLowerCase() !== 'sc') {
          metadataSources.push({
            status: 404,
            correlationId: requestError?.correlationId,
          });
          return {
            failedRedirect: true,
            metadataSources,
          };
        }

        const codeResult = await generateAssociationCode(token);
        association = codeResult.data;
        metadataSources.push(codeResult);
      }

      return {
        watchData,
        association,
        businessData,
        productImgUrl,
        tokenResolved: true,
        metadataSources,
      };
    } catch (error) {
      return {
        error,
        tokenResolved: true,
      };
    }
  })();

  return earlyPromise;
};

export function useAppInitialization() {
  const trackFetching = useCallback((properties = {}) => {
    trackAppEvent('Fetch', properties);
  }, []);

  const { authParams } = useAuthParams();

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    productImgUrl: Placeholder,
  });

  useEffect(() => {
    let cancelled = false;

    const redirectToEntryPoint = () => {
      window.location.replace(configData.redirectUrl);
    };

    const run = async () => {
      const startedAt = earlyPromiseStartedAt || Date.now();

      try {
        trackFetching({
          stage: 'initialization_started',
          hasStoredToken: Boolean(sessionStorage.getItem('bearer')),
          hasAuthParams: hasAnyAuthParams(authParams),
          cardType: authParams?.cardType || '',
        });

        // Resolve the early preloaded promise (or trigger a fallback)
        const result = earlyPromise
          ? await earlyPromise
          : await startEarlyInitialization(authParams);

        if (cancelled) return;

        if (result.failedRedirect) {
          if (result.metadataSources) {
            result.metadataSources.forEach((src) => {
              dispatch({
                type: 'SET_REQUEST_METADATA',
                payload: extractRequestMetadata(src),
              });
            });
          }
          clearAuthParams();
          redirectToEntryPoint();
          return;
        }

        if (result.error) {
          throw result.error;
        }

        // Hydrate React application state directly from early results
        if (result.metadataSources) {
          result.metadataSources.forEach((src) => {
            dispatch({
              type: 'SET_REQUEST_METADATA',
              payload: extractRequestMetadata(src),
            });
          });
        }

        if (result.watchData) {
          dispatch({
            type: 'SET_WATCH_DATA',
            payload: result.watchData,
          });
        }

        if (result.association) {
          dispatch({
            type: 'SET_ASSOCIATION',
            payload: result.association,
          });
        }

        if (result.businessData) {
          dispatch({
            type: 'SET_BUSINESS_DATA',
            payload: result.businessData,
          });
        }

        if (result.productImgUrl) {
          dispatch({
            type: 'SET_PRODUCT_IMAGE',
            payload: result.productImgUrl,
          });
        }

        clearAuthParams();
        await waitForMinimumLoadingTime(startedAt, UI_LOADING_MIN_DURATION_MS);

        if (!cancelled) {
          dispatch({ type: 'FINISH_LOADING' });
        }
      } catch (requestError) {
        if (!cancelled) {
          dispatch({
            type: 'FAIL',
            payload: extractRequestMetadata(requestError),
          });
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [authParams, trackFetching]);

  return {
    watchData: state.watchData,
    businessData: state.businessData,
    association: state.association,
    generatedCode: state.generatedCode,
    loading: state.loading,
    error: state.error,
    status: state.status,
    correlation: state.correlation,
    productImgUrl: state.productImgUrl,
    cardType: authParams?.cardType,
  };
}
